/**
 * Os três blocos do dashboard que substituíram a barra de "motivos de não
 * efetivação" — que, com dois motivos de valor 1, era uma lista com enfeite.
 *
 *   PrecisaDeVoce  o que parou de andar e depende de alguém
 *   Funil          onde as famílias somem
 *   OrigemLeads    de onde elas vieram
 */
import { post } from "../lib/api";
import { dataHoraCurta } from "../lib/formato";
import { telefoneExibicao } from "../lib/paises";
import { linkWhatsapp, mensagemAcompanhamento } from "../lib/mensagensRetomada";
import type { Kpis, Origens, Pendencias, VisitaResumo } from "../lib/tipos";

const diasDesde = (iso: string) =>
  Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);

function Grupo({
  cor,
  fundo,
  borda,
  titulo,
  explicacao,
  itens,
  detalhe,
  onAbrir,
  onWhatsapp,
}: {
  cor: string;
  fundo: string;
  borda: string;
  titulo: string;
  explicacao: string;
  itens: VisitaResumo[];
  detalhe: (v: VisitaResumo) => string;
  onAbrir: (id: string) => void;
  /** Quando presente, cada linha ganha o botão de WhatsApp manual. */
  onWhatsapp?: (v: VisitaResumo) => void;
}) {
  if (itens.length === 0) return null;
  return (
    <div style={{ background: fundo, border: `1px solid ${borda}`, borderRadius: 10, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 13, color: cor }}>{titulo}</strong>
        <span style={{ background: cor, color: "#fff", borderRadius: 20, padding: "1px 8px", fontSize: 11, fontWeight: 700 }}>
          {itens.length}
        </span>
        <span style={{ fontSize: 11, color: "var(--texto-suave)" }}>{explicacao}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {itens.slice(0, 6).map((v) => (
          <div
            key={v.id}
            style={{ display: "flex", alignItems: "center", gap: 8, background: "#fff", border: `1px solid ${borda}`, borderRadius: 8, padding: "4px 6px 4px 10px" }}
          >
            <button
              onClick={() => onAbrir(v.id)}
              title="Abrir a visita"
              style={{ display: "flex", alignItems: "center", gap: 10, background: "none", border: "none", padding: "4px 0", cursor: "pointer", textAlign: "left", flex: 1, minWidth: 0, flexWrap: "wrap" }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, flex: 1, minWidth: 150 }}>
                {v.nomeAluno}
                <span style={{ fontWeight: 400, color: "var(--texto-suave)" }}> — {v.nomeResponsavel}</span>
              </span>
              <span style={{ fontSize: 11, color: "var(--texto-suave)" }}>{telefoneExibicao(v.telefoneResponsavel, v.paisTelefone)}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: cor, whiteSpace: "nowrap" }}>{detalhe(v)}</span>
            </button>
            {onWhatsapp && v.telefoneResponsavel && (
              <button
                onClick={() => onWhatsapp(v)}
                title="Abrir o WhatsApp desta família com a mensagem pronta"
                style={{ flexShrink: 0, background: "#0e8c3a", color: "#fff", border: "none", borderRadius: 6, padding: "5px 9px", fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
              >
                ↗ WhatsApp
              </button>
            )}
          </div>
        ))}
        {itens.length > 6 && (
          <div style={{ fontSize: 11, color: "var(--texto-suave)", paddingLeft: 4 }}>
            e mais {itens.length - 6}…
          </div>
        )}
      </div>
    </div>
  );
}

export function PrecisaDeVoce({
  dados, onAbrir, aoContatar,
}: {
  dados: Pendencias;
  onAbrir: (id: string) => void;
  /** Recarrega o dashboard depois do contato — a linha some da lista. */
  aoContatar: () => void | Promise<void>;
}) {
  const novas = dados.novasDoSite ?? [];

  /**
   * Abre o WhatsApp da família com o texto pronto e registra o contato.
   *
   * O registro é o que faz a linha sair do alerta: "sem movimento" passa a
   * considerar a conversa, não só a edição do cadastro. Se o registro falhar,
   * a conversa já abriu — não vale atrapalhar o envio por causa disso.
   */
  async function abrirWhatsapp(v: VisitaResumo) {
    const link = linkWhatsapp(v.telefoneResponsavel, mensagemAcompanhamento(v), v.paisTelefone);
    if (!link) return;
    window.open(link, "_blank", "noopener");
    try {
      await post(`/api/retomada/${v.id}/contato`, { origem: "pendencias" });
      await aoContatar();
    } catch {
      /* silencioso de propósito */
    }
  }
  const total = dados.atrasadas.length + dados.paradas.length + dados.semRetorno.length + novas.length;

  return (
    <div className="cartao" style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#344054" }}>📌 Precisa de você</div>
        <div style={{ fontSize: 12, color: "var(--texto-suave)" }}>
          {total === 0 ? "nada parado" : `${total} visita(s) esperando alguém`}
        </div>
      </div>

      {total === 0 ? (
        <div style={{ fontSize: 13, color: "var(--texto-suave)", padding: "8px 2px" }}>
          Nenhuma visita parada. Toda visita agendada que já passou teve baixa, e ninguém está
          esperando retorno há dias.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Grupo
            cor="#0e8c3a" fundo="#f0faf4" borda="#c6e9d4"
            titulo="Agendaram pelo site"
            explicacao="a família marcou sozinha e ninguém da equipe abriu ainda"
            itens={novas}
            detalhe={(v) => dataHoraCurta(v.inicioEm)}
            onAbrir={onAbrir}
          />
          <Grupo
            cor="#b42318" fundo="#fef3f2" borda="#fecdca"
            titulo="Passou da data e continua Agendada"
            explicacao="a visita aconteceu (ou não) e ninguém registrou o desfecho"
            itens={dados.atrasadas}
            detalhe={(v) => dataHoraCurta(v.inicioEm)}
            onAbrir={onAbrir}
          />
          <Grupo
            cor="#b54708" fundo="#fffaeb" borda="#fedf89"
            titulo="Em andamento, sem movimento"
            explicacao={`ninguém mexe há mais de ${dados.diasParada} dias`}
            itens={dados.paradas}
            detalhe={(v) => `${diasDesde(v.atualizadoEm)} dias parada`}
            onAbrir={onAbrir}
            onWhatsapp={abrirWhatsapp}
          />
          <Grupo
            cor="#5925dc" fundo="#f4f3ff" borda="#d9d6fe"
            titulo="Em pesquisa, sem retorno"
            explicacao={`a família não respondeu há mais de ${dados.diasPesquisa} dias`}
            itens={dados.semRetorno}
            detalhe={(v) => `${diasDesde(v.atualizadoEm)} dias`}
            onAbrir={onAbrir}
            onWhatsapp={abrirWhatsapp}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Funil por status atual.
 *
 * Não é o funil "de verdade" — esse exigiria contar cada passagem de etapa na
 * trilha de auditoria, e as 74 visitas importadas do Firestore não têm essa
 * história. Aqui cada visita é contada até a etapa onde ela está hoje, e as
 * perdidas contam até "em negociação", porque foi até onde chegaram.
 */
const ETAPAS: Array<{ rotulo: string; cor: string; status: string[] }> = [
  { rotulo: "Agendadas", cor: "#1570ef", status: ["AGENDADO", "EM_ANDAMENTO", "EM_PESQUISA", "RECALL", "FINANCEIRO", "FECHOU_MATRICULA", "OPTOU_OUTRA_ESCOLA", "CANCELADO"] },
  { rotulo: "Atendidas", cor: "#0e8c3a", status: ["EM_ANDAMENTO", "EM_PESQUISA", "RECALL", "FINANCEIRO", "FECHOU_MATRICULA", "OPTOU_OUTRA_ESCOLA"] },
  { rotulo: "Em negociação", cor: "#dc6803", status: ["EM_PESQUISA", "RECALL", "FINANCEIRO", "FECHOU_MATRICULA", "OPTOU_OUTRA_ESCOLA"] },
  { rotulo: "Matriculadas", cor: "#079455", status: ["FECHOU_MATRICULA"] },
];

export function Funil({ kpis }: { kpis: Kpis }) {
  const conta = (lista: string[]) => lista.reduce((s, st) => s + (kpis.porStatus[st] ?? 0), 0);
  const etapas = ETAPAS.map((e) => ({ ...e, quantidade: conta(e.status) }));
  const base = etapas[0].quantidade;

  return (
    <div className="cartao" style={{ flex: "1 1 380px", minWidth: 320 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#344054" }} title="Cada visita é contada até a etapa em que está hoje">
          🔻 Funil
        </div>
        <div style={{ fontSize: 12, color: "var(--texto-suave)" }}>
          Conversão: <strong style={{ color: "#079455" }}>{kpis.taxaConversao}%</strong>
          {" · "}Perda: <strong style={{ color: "#d92d20" }}>{kpis.taxaPerda}%</strong>
        </div>
      </div>

      {base === 0 ? (
        <div style={{ fontSize: 13, color: "var(--texto-suave)" }}>Nenhuma visita no período.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {etapas.map((e, i) => {
            const anterior = i === 0 ? null : etapas[i - 1].quantidade;
            const perdeu = anterior !== null && anterior > 0 ? anterior - e.quantidade : 0;
            return (
              <div key={e.rotulo} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 104, fontSize: 12, color: "#475467", fontWeight: 500, textAlign: "right", flexShrink: 0 }}>
                  {e.rotulo}
                </div>
                <div style={{ flex: 1, background: "#f2f4f7", borderRadius: 6, height: 24, overflow: "hidden", position: "relative" }}>
                  <div style={{ width: `${(e.quantidade / base) * 100}%`, height: "100%", background: e.cor, borderRadius: 6, minWidth: e.quantidade > 0 ? 3 : 0 }} />
                </div>
                <div style={{ width: 74, fontSize: 12, textAlign: "right", flexShrink: 0 }}>
                  <strong style={{ color: e.cor }}>{e.quantidade}</strong>
                  <span style={{ color: "var(--texto-suave)" }}> · {Math.round((e.quantidade / base) * 100)}%</span>
                </div>
                <div style={{ width: 54, fontSize: 11, color: perdeu > 0 ? "#d92d20" : "transparent", flexShrink: 0 }}>
                  {perdeu > 0 ? `−${perdeu}` : "—"}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function OrigemLeads({ dados }: { dados: Origens }) {
  const lista = dados.origens.slice(0, 6);
  const maior = lista[0]?.total ?? 1;
  const naoInformado = dados.origens.find((o) => o.origem === "Não informado")?.total ?? 0;
  const pctVazio = dados.total ? Math.round((naoInformado / dados.total) * 100) : 0;

  return (
    <div className="cartao" style={{ flex: "1 1 320px", minWidth: 300 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#344054" }}>🌱 Origem dos leads</div>
        <div style={{ fontSize: 12, color: "var(--texto-suave)" }}>
          Pelo site: <strong style={{ color: "#0e8c3a" }}>{dados.pelosite ?? 0}</strong>
          {" · "}Por indicação: <strong style={{ color: "#0e8c3a" }}>{dados.comIndicacao}</strong>
        </div>
      </div>

      {dados.total === 0 ? (
        <div style={{ fontSize: 13, color: "var(--texto-suave)" }}>Nenhuma visita no período.</div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
            {lista.map((o) => (
              <div key={o.origem} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div title={o.origem} style={{ width: 110, fontSize: 12, color: "#475467", textAlign: "right", flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {o.origem}
                </div>
                <div style={{ flex: 1, background: "#f2f4f7", borderRadius: 6, height: 20, overflow: "hidden" }}>
                  <div style={{ width: `${(o.total / maior) * 100}%`, height: "100%", background: o.origem === "Não informado" ? "#d0d5dd" : "#0e8c3a", borderRadius: 6 }} />
                </div>
                <div style={{ width: 84, fontSize: 11, textAlign: "right", flexShrink: 0 }}>
                  <strong>{o.total}</strong>
                  <span style={{ color: "var(--texto-suave)" }}> · {o.matriculas} mat.</span>
                </div>
              </div>
            ))}
          </div>

          {pctVazio >= 50 && (
            <div style={{ marginTop: 12, fontSize: 11, color: "#b54708", background: "#fffaeb", border: "1px solid #fedf89", borderRadius: 8, padding: "8px 10px" }}>
              {pctVazio}% das visitas estão sem "Onde conheceu o COC". Esse campo é a matéria-prima
              da prospecção — enquanto ficar vazio, não dá para saber o que traz família.
            </div>
          )}
        </>
      )}
    </div>
  );
}
